import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useEffect } from 'react';

import {
  trackMessageSendFailed,
  trackMessageSent,
} from '../../../../../../services/analytics/marketplaceEvents';
import { ContactMessageEntry } from '../../../../../../services/marketplace/generated/kubb/models/ContactMessageEntry';
import { OfferActionKind, OfferCardView } from '../offers/utils';
import ConversationHeader from './ConversationHeader';
import MessageComposer from './MessageComposer';
import MessageList from './MessageList';
import { useKeyboardInset } from './useKeyboardInset';
import { useMarkSeen } from './useMarkSeen';
import { useSendMessage } from './useSendMessage';
import {
  ConversationTarget,
  PLATFORM_LABEL,
  getUnreadThreadIds,
  targetHistory,
  targetLabel,
} from './utils';

type ConversationThreadDrawerProps = {
  target: ConversationTarget | null;
  thread: ContactMessageEntry[] | undefined;
  externalId: string;
  leadId: string;
  onClose: () => void;
  onAction?: (kind: OfferActionKind, card: OfferCardView) => void;
  threadReanchorSignal?: number;
};

const ConversationThreadDrawer = ({
  target,
  thread,
  externalId,
  leadId,
  onClose,
  onAction,
  threadReanchorSignal,
}: ConversationThreadDrawerProps) => {
  const placement =
    useBreakpointValue<'bottom' | 'right'>({ base: 'bottom', md: 'right' }) ?? 'right';
  const keyboardInset = useKeyboardInset();
  const sendMessage = useSendMessage(externalId);
  const markSeen = useMarkSeen(externalId);

  const history = targetHistory(target);
  useEffect(() => {
    if (target == null) return;
    markSeen(getUnreadThreadIds(thread, history));
  }, [target, thread, history, markSeen]);

  const handleSend = (openTarget: ConversationTarget) => (body: string) => {
    const threadId = thread?.[thread.length - 1]?.threadId;
    const data = threadId != null ? { body, threadId } : { body, orgName: targetLabel(openTarget) };
    return sendMessage(data).then(
      () => trackMessageSent(leadId, openTarget, body, thread),
      (error) => {
        trackMessageSendFailed(leadId, openTarget, body);
        throw error;
      },
    );
  };

  return (
    <Drawer
      isOpen={target != null}
      onClose={onClose}
      placement={placement}
      size={{ base: 'md', md: 'lg' }}
    >
      <DrawerOverlay />
      <DrawerContent
        h={{ base: '92dvh', md: '100%' }}
        maxH={{ base: '92dvh', md: '100%' }}
        borderTopRadius={{ base: '16px', md: undefined }}
        pb={keyboardInset > 0 ? `${keyboardInset}px` : 'env(safe-area-inset-bottom)'}
      >
        <DrawerCloseButton top="16px" right="16px" />
        <DrawerHeader borderBottomWidth="1px" borderBottomColor="grayMediumBackground" pr="48px">
          {target != null && <ConversationHeader target={target} />}
        </DrawerHeader>
        <DrawerBody display="flex" flexDirection="column" p={0}>
          {target != null && (
            <MessageList
              target={target}
              thread={thread}
              onAction={onAction}
              threadReanchorSignal={threadReanchorSignal}
            />
          )}
          {target != null && (
            <MessageComposer
              onSend={handleSend(target)}
              placeholder={target.kind === 'platform' ? `Message ${PLATFORM_LABEL}` : undefined}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ConversationThreadDrawer;
